import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { BaseWorkSafeBcSubSection, IBaseWorkSafeBcSubSectionProps } from "../BaseWorkSafeBcSubSection";
import { WorkSafeBcRehabilitationProgram } from "../Constants/WorkSafeBcRehabilitationProgram";
import { WorkSafeBcReturnToWorkTimeSpan } from "../Constants/WorkSafeBcReturnToWorkTimeSpan";
import { WorkSafeBcFormValidator } from "../Models/WorkSafeBcFormValidator";

class WorkSafeBcReturnToWorkPlanningSection extends BaseWorkSafeBcSubSection<IBaseWorkSafeBcSubSectionProps, never> {
    public render(): JSX.Element {
        const data = this.props.model;
        const validator = new WorkSafeBcFormValidator(data);

        return (
            <div className="flex-grow-container subsection-border-top">
                <div className="row">
                    <div className="col-md-12">
                        <label className="form-label bold f16px">{this.getString("WorkSafeBc_ReturnToWorkPlanningLabel")}</label>
                    </div>
                </div>
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_IsWorkerMedicallyCapableLabel",
                        inline: true,
                        required: true,
                        isEditMode: true
                    },
                    <InlineOptionsListControl.YesOrNo
                        id={`yesOrNo_IsWorkerMedicallyCapable_${this.props.formId}`}
                        value={data.IsWorkerMedicallyCapableToReturnToWork}
                        onChange={option => this.props.mutateModelState(s => s.IsWorkerMedicallyCapableToReturnToWork = option?.Key)}
                        disabled={!this.props.isEditMode}
                        allowDeselect
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_CurrentPhysicalPsychologicalRestrictionsLabel",
                        value: data.CurrentPhysicalOrPsychologicalRestrictions,
                        required: data.IsWorkerMedicallyCapableToReturnToWork === false
                    },
                    <TextboxControl
                        value={data.CurrentPhysicalOrPsychologicalRestrictions}
                        onChange={value => this.props.mutateModelState(s => s.CurrentPhysicalOrPsychologicalRestrictions = value)}
                        maxLength={200}
                        valid={validator.isCurrentPhysicalOrPsychologicalRestrictionsValid()}
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSaveBc_WhenCanWorkerReturnToWorkLabel",
                        required: true,
                        isEditMode: true
                    },
                    <InlineOptionsListControl
                        id={`WorkSaveBc_WhenCanWorkerReturnToWork_${this.props.formId}`}
                        items={this.getReturnToWorkTimeSpanOptions()}
                        value={data.DurationOfWorkReturn}
                        onChange={option => this.props.mutateModelState(s => s.DurationOfWorkReturn = option?.Key)}
                        disabled={!this.props.isEditMode}
                        allowDeselect
                    />
                )}

                <div className="row margin-sm-bottom">
                    <div className="col-md-12 regular-practitioner-injuryreport padding-none-left">
                        {this.renderFields(
                            {
                                labelValueKey: "WorkSafeBc_IsWorkerReadyForRehabilitationProgramLabel",
                                inline: true,
                                hasParentClassName: true,
                                required: true,
                                isEditMode: true
                            },
                            <InlineOptionsListControl.YesOrNo
                                id={`yesOrNo_IsWorkerReadyForRehabilitationProgram_${this.props.formId}`}
                                value={data.IsWorkerReadyForRehab}
                                onChange={option => this.props.mutateModelState(s => s.IsWorkerReadyForRehab = option?.Key)}
                                disabled={!this.props.isEditMode}
                                allowDeselect
                            />
                        )}
                        {this.renderFields(
                            {
                                labelValueKey: "WorkSafeBc_IfYesSelectLabel",
                                inline: true,
                                hasParentClassName: true,
                                required: data.IsWorkerReadyForRehab,
                                isEditMode: true
                            },
                            <InlineOptionsListControl
                                id={`WorkSaveBc_RehabProgramSelect_${this.props.formId}`}
                                items={this.getRehabilitationOptions()}
                                value={data.RehabilitationProgram}
                                onChange={option => this.props.mutateModelState(s => s.RehabilitationProgram = option?.Key)}
                                disabled={!this.props.isEditMode}
                                allowDeselect
                            />
                        )}
                    </div>
                </div>
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_DoYouWishToConsultWorkSafeBcPhysicianLabel",
                        inline: true,
                        required: true,
                        isEditMode: true
                    },
                    <InlineOptionsListControl.YesOrNo
                        id={`yesOrNo_DoYouWishToConsultWorkSafeBcPhysician_${this.props.formId}`}
                        value={data.ConsultWorkSafeBcPhysicianOrNurseAdvisor}
                        onChange={option => this.props.mutateModelState(s => s.ConsultWorkSafeBcPhysicianOrNurseAdvisor = option?.Key)}
                        disabled={!this.props.isEditMode}
                        allowDeselect
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "WorkSafeBc_IfPossibleEstimateDateOfMaximalRecoveryLabel",
                        value: DateHelper.formatDate(data.EstimatedDateOfMedicalRecovery, this.dateFormat)
                    },
                    <DateSelector
                        id={`IfPossibleEstimateDateOfMaximalRecovery_${this.props.formId}`}
                        onChangeCallback={date => this.props.mutateModelState(s => s.EstimatedDateOfMedicalRecovery = date)}
                        date={data.EstimatedDateOfMedicalRecovery}
                        dateFormat={this.dateFormatForDateSelector}
                        width={150}
                    />
                )}
            </div>
        );
    }

    private getReturnToWorkTimeSpanOptions(): InlineOption<WorkSafeBcReturnToWorkTimeSpan>[] {
        return [
            new InlineOption(WorkSafeBcReturnToWorkTimeSpan.CurrentlyAtWork, this.getString("WorkSafeBc_ReturnToWorkTimeSpanOption_CurrentlyAtWork")),
            new InlineOption(WorkSafeBcReturnToWorkTimeSpan.OneToSixDays, this.getString("WorkSafeBc_ReturnToWorkTimeSpanOption_1to6Days")),
            new InlineOption(WorkSafeBcReturnToWorkTimeSpan.SevenToThirteenDays, this.getString("WorkSafeBc_ReturnToWorkTimeSpanOption_7to13Days")),
            new InlineOption(WorkSafeBcReturnToWorkTimeSpan.FourteenToTwentyDays, this.getString("WorkSafeBc_ReturnToWorkTimeSpanOption_14to20Days")),
            new InlineOption(WorkSafeBcReturnToWorkTimeSpan.GreaterThan20Days, this.getString("WorkSafeBc_ReturnToWorkTimeSpanOption_GreaterThan20Days"))
        ];
    }

    private getRehabilitationOptions(): InlineOption<WorkSafeBcRehabilitationProgram>[] {
        return [
            new InlineOption(WorkSafeBcRehabilitationProgram.WCP, this.getString("WorkSafeBc_RehabilitationProgramOption_WCP")),
            new InlineOption(WorkSafeBcRehabilitationProgram.Other, this.getString("OtherLabel"))
        ];
    }
}

export { WorkSafeBcReturnToWorkPlanningSection };
